import { APP_SETTINGS } from '../config/appSettings'
import type { NativeOAuthHandlingResult } from './authProvider'
import type { AppEnvironment } from '../supabase/config'
import type { NativeOAuthAttempt } from '../supabase/authStorage'
import { createId } from '../utils/id'

const CALLBACK_PATH = '/auth/callback/android'

const CALLBACK_HOSTS: Record<AppEnvironment, string> = {
  development: 'staging.app.minimany.app',
  production: 'app.minimany.app',
}

export type NativeOAuthCallback =
  | { kind: 'code'; code: string }
  | { kind: 'provider-error'; code: string }
  | { kind: 'invalid'; reason: 'url' | 'attempt' | 'expired' | 'payload' }

export function nativeOAuthCallbackBase(environment: AppEnvironment): string {
  return `https://${CALLBACK_HOSTS[environment]}${CALLBACK_PATH}`
}

export function authBusyAfterNativeOAuthResult(
  wasBusy: boolean,
  result: NativeOAuthHandlingResult,
): boolean {
  return result.status === 'ignored' ? wasBusy : false
}

export function createNativeOAuthAttempt(
  environment: AppEnvironment,
  returnPath: string,
  now = Date.now(),
  id = createId(),
): NativeOAuthAttempt {
  const callback = new URL(nativeOAuthCallbackBase(environment))
  callback.searchParams.set('attempt', id)
  return {
    key: 'active',
    id,
    redirectUrl: callback.toString(),
    returnPath: returnPath.startsWith('/') ? returnPath : '/',
    createdAt: now,
    expiresAt: now + APP_SETTINGS.auth.nativeOAuthAttemptTtlMs,
    authorizationCode: null,
  }
}

export function parseNativeOAuthCallback(
  rawUrl: string,
  environment: AppEnvironment,
  attempt: NativeOAuthAttempt,
  now = Date.now(),
): NativeOAuthCallback {
  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    return { kind: 'invalid', reason: 'url' }
  }

  const expected = new URL(nativeOAuthCallbackBase(environment))
  if (
    url.protocol !== 'https:'
    || url.hostname !== expected.hostname
    || url.port !== ''
    || url.username !== ''
    || url.password !== ''
    || url.pathname !== expected.pathname
    || url.hash !== ''
  ) return { kind: 'invalid', reason: 'url' }

  if (url.searchParams.get('attempt') !== attempt.id) {
    return { kind: 'invalid', reason: 'attempt' }
  }
  if (now > attempt.expiresAt) return { kind: 'invalid', reason: 'expired' }

  const code = url.searchParams.get('code')?.trim()
  const error = url.searchParams.get('error')?.trim()
  if (code && !error) return { kind: 'code', code }
  if (error && !code) return { kind: 'provider-error', code: error }
  return { kind: 'invalid', reason: 'payload' }
}
